import {
  cacheKeys,
  CLEAR_ASSETS_LOCATION,
  useCurrentAccount,
  useCustomMutation,
  useGrippContext,
} from '@gripp/shared-logic';
import {
  Button,
  ConfirmationOverlay,
  Spacing,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';

type Props = {
  assetIds: string[];
  workspaceId: string;
};

export const AssetsClearLocationButton = ({ assetIds, workspaceId }: Props) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const { logger } = useGrippContext();
  const { setColumnOverlay } = useSlideLayout();
  const queryClient = useQueryClient();
  const { mutateAsync } = useCustomMutation({ query: CLEAR_ASSETS_LOCATION });

  if (!account?.isGlobalAdmin()) {
    return null;
  }

  const hideOverlay = () => setColumnOverlay(null);

  const handleConfirm = async () => {
    try {
      await mutateAsync({ input: { assetIds, workspaceId } });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('asset').all,
      });
      hideOverlay();
    } catch (error) {
      logger?.error(error);
      setColumnOverlay(
        <ConfirmationOverlay
          title={t('assets.clearLocation.failedTitle')}
          subtitle={t('assets.clearLocation.failedSubtitle')}
          confirmLabel={t('common.ok')}
          confirmState="active"
          onConfirm={hideOverlay}
          cancelLabel={t('common.close')}
          onCancel={hideOverlay}
        />
      );
    }
  };

  const handlePress = () => {
    setColumnOverlay(
      <ConfirmationOverlay
        title={t('assets.clearLocation.title')}
        subtitle={t('assets.clearLocation.subtitle')}
        confirmLabel={t('assets.clearLocation.button')}
        onConfirm={handleConfirm}
        cancelLabel={t('common.cancel')}
        onCancel={hideOverlay}
      />
    );
  };

  return (
    <View style={styles.container}>
      <Button
        variant="rectangular"
        state="outline"
        style={styles.button}
        onPress={handlePress}
        size="large"
        testID="assets-selection-clear-location"
      >
        {t('assets.clearLocation.button')}
      </Button>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Spacing.baseMargin.base,
    paddingBottom: Spacing.baseMargin.base,
  },
  button: {
    width: '100%',
  },
});
